import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { CartItem, Perfume } from '@/types'
import { getCart, syncCart } from '@/lib/api'

interface CartState {
  items: CartItem[]
  giftWrap: boolean
  sampleCredit: number
  isCartOpen: boolean
  sessionId: string | null
  hydrated: boolean
  addItem: (perfume: Perfume, sizeId: string) => void
  removeItem: (lineId: string) => void
  updateQuantity: (lineId: string, quantity: number) => void
  setItems: (items: CartItem[]) => void
  setSessionId: (id: string) => void
  setHydrated: (hydrated: boolean) => void
  toggleGiftWrap: () => void
  toggleCart: (open?: boolean) => void
  clearCart: () => void
  subtotal: () => number
  total: () => number
}

function generateId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`
}

function generateSessionId(): string {
  return `ls-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`
}

function scheduleSync(items: CartItem[], sessionId: string | null) {
  if (typeof window === 'undefined') return
  // Fire-and-forget server sync. Errors are silent so the UI stays fast.
  syncCart(items, sessionId).catch(() => {})
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      giftWrap: false,
      sampleCredit: 0,
      isCartOpen: false,
      sessionId: null,
      hydrated: false,

      addItem: (perfume, sizeId) => {
        const size = perfume.sizes.find((s) => s.id === sizeId)
        if (!size) return

        set((state) => {
          const existing = state.items.find(
            (i) => i.perfumeId === perfume.id && i.sizeId === sizeId
          )
          const lineId = existing?.id ?? generateId()

          let credit = state.sampleCredit
          if (!size.size.includes('sample') && !perfume.isDiscoverySet) {
            const sample = state.items.find(
              (i) => i.perfumeId === perfume.id && i.sizeLabel.includes('sample')
            )
            if (sample) credit += sample.price
          }

          let nextItems: CartItem[]
          if (existing) {
            nextItems = state.items.map((i) =>
              i.id === existing.id
                ? { ...i, quantity: i.quantity + 1 }
                : i
            )
          } else {
            nextItems = [
              ...state.items,
              {
                id: lineId,
                perfumeId: perfume.id,
                perfumeName: perfume.name,
                sizeId,
                sizeLabel: size.size,
                price: size.price,
                quantity: 1,
                image: perfume.images[0],
              },
            ]
          }

          scheduleSync(nextItems, state.sessionId)

          return {
            ...state,
            sampleCredit: credit,
            items: nextItems,
          }
        })
      },

      removeItem: (lineId) => {
        set((state) => {
          const nextItems = state.items.filter((i) => i.id !== lineId)
          scheduleSync(nextItems, state.sessionId)
          return { ...state, items: nextItems }
        })
      },

      updateQuantity: (lineId, quantity) => {
        if (quantity < 1) {
          get().removeItem(lineId)
          return
        }
        set((state) => {
          const nextItems = state.items.map((i) =>
            i.id === lineId ? { ...i, quantity } : i
          )
          scheduleSync(nextItems, state.sessionId)
          return { ...state, items: nextItems }
        })
      },

      setItems: (items) => {
        set((state) => {
          scheduleSync(items, state.sessionId)
          return { ...state, items }
        })
      },

      setSessionId: (id) => set({ sessionId: id }),
      setHydrated: (hydrated) => set({ hydrated }),

      toggleGiftWrap: () => set((state) => ({ ...state, giftWrap: !state.giftWrap })),
      toggleCart: (open) =>
        set((state) => ({
          ...state,
          isCartOpen: open !== undefined ? open : !state.isCartOpen,
        })),
      clearCart: () =>
        set((state) => {
          scheduleSync([], state.sessionId)
          return { ...state, items: [], giftWrap: false, sampleCredit: 0 }
        }),

      subtotal: () => {
        return get().items.reduce((sum, i) => sum + i.price * i.quantity, 0)
      },

      total: () => {
        const { items, giftWrap, sampleCredit } = get()
        const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0)
        const wrapFee = giftWrap ? 2500 : 0
        return Math.max(0, subtotal + wrapFee - sampleCredit)
      },
    }),
    {
      name: 'little-sky-cart',
      partialize: (state) => ({
        items: state.items,
        giftWrap: state.giftWrap,
        sampleCredit: state.sampleCredit,
        sessionId: state.sessionId,
        hydrated: false,
      }),
    }
  )
)

export async function hydrateCartFromServer(store: typeof useCartStore = useCartStore): Promise<void> {
  const state = store.getState()
  const sessionId = state.sessionId || generateSessionId()
  if (!state.sessionId) {
    store.setState({ sessionId })
  }

  try {
    const server = await getCart(sessionId)
    if (server === null) {
      // The read failed. Leave the local bag exactly as it is rather than assuming
      // the server is empty — see getCart's note on destructive replaces.
      store.setState({ hydrated: true })
      return
    }

    if (server.session_id && server.session_id !== sessionId) {
      store.setState({ sessionId: server.session_id })
    }

    const serverItems = Array.isArray(server.items) ? server.items : []
    const localItems = state.items
    if (serverItems.length === 0) {
      store.setState({ hydrated: true })
      // This account has no server-side cart yet, but the bag on screen has items.
      // They were added before signing in — as a guest, under a session id that
      // nothing migrates on login — or a fire-and-forget sync failed silently.
      // Push them up, or the account cart (and the mobile app reading it) stays
      // empty while this page goes on showing items that were never persisted.
      if (localItems.length > 0) scheduleSync(localItems, store.getState().sessionId)
      return
    }

    if (localItems.length === 0) {
      store.setState({ items: serverItems as CartItem[], hydrated: true })
      return
    }

    // Merge: server items not already in the local bag are added.
    const localKeys = new Set(localItems.map((i) => `${i.perfumeId}:${i.sizeId}`))
    const merged = [
      ...localItems,
      ...serverItems
        .filter((i) => !localKeys.has(`${i.perfumeId}:${i.sizeId}`))
        .map((i) => ({ ...i, id: generateId() }) as CartItem),
    ]
    store.setState({ items: merged, hydrated: true })
    scheduleSync(merged, store.getState().sessionId)
  } catch {
    store.setState({ hydrated: true })
  }
}
